import React, { useEffect, useMemo, useState } from 'react';
import { fournisseurs as api } from '../services/api';
import { montant, moisCourt, usePanneau, SelecteurUnite } from './ventesUtils';
import { CATS, DIMS, dimObj, deltaPct, signeEur, signePct1, useFiltresFourn, PanneauFourn, Avertissements, ModaleEcritures } from './fournisseursUtils';
import { sequentielle, divergente, Echelle } from './heatmap';
import './Ventes.css';
import './Fournisseurs.css';

// ─── Fournisseurs & charges — Analyse croisée ────────────────────────────────
// Matrice dimension × mois (même gabarit que VN/VO/APV) : montant HT du mois,
// ou écart N-1 (€ / %) cellule par cellule. Clic sur une ligne = détail par la
// sous-dimension (regroupement → fournisseurs, fournisseur → comptes).

const MESURES = [
  { code: 'montant', label: 'Montant HT' },
  { code: 'delta', label: 'Δ N-1 €' },
  { code: 'deltaPct', label: 'Δ N-1 %' },
];
const cellVal = (o, i, m) => m === 'montant' ? o.m[i] : m === 'delta' ? (o.n1[i] ? o.m[i] - o.n1[i] : null) : deltaPct(o.m[i], o.n1[i]);
const totVal = (o, m) => { const t = o.m.reduce((a, b) => a + b, 0), t1 = o.n1.reduce((a, b) => a + b, 0); return m === 'montant' ? t : m === 'delta' ? (t1 ? t - t1 : null) : deltaPct(t, t1); };
// « Δ N-1 % » est un pourcentage : il ne suit pas le sélecteur.
const MONETAIRES = ['montant', 'delta'];
const fmt = (v, m, u) => v == null ? '—' : m === 'montant' ? montant(v, u) : m === 'delta' ? signeEur(v, u) : signePct1(v);

// Montant : séquentielle. Écarts : divergente de sens INVERSE — ici une hausse
// est une charge qui augmente, donc une mauvaise nouvelle. Bornes au 90e
// centile pour qu'un fournisseur exceptionnel n'écrase pas toute la matrice.
const couleur = (v, m, st) => (m === 'montant'
  ? sequentielle(v, st.max)
  : divergente(v, { mid: 0, min: -(st.lo || 1), max: st.hi || 1, sens: -1 }));
const centile = (arr, p) => { if (!arr.length) return 0; const s = [...arr].sort((a, b) => a - b); return s[Math.min(s.length - 1, Math.floor(p * (s.length - 1)))]; };
function statsDe(rows, m) {
  const vals = []; rows.forEach((r) => r.m.forEach((_, i) => { const v = cellVal(r, i, m); if (v != null) vals.push(v); }));
  if (m === 'montant') return { max: Math.max(...vals, 1) };
  return { hi: centile(vals.filter((v) => v > 0), 0.9), lo: -centile(vals.filter((v) => v < 0), 0.1) };
}

export default function FournisseursAnalyse() {
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  const [mesure, setMesure] = useState('montant');
  const [unite, setUnite] = useState('k');
  const [dimension, setDimension] = useState('regroupement');
  const [drill, setDrill] = useState({});
  const [open, setOpen] = useState(null);
  const [modale, setModale] = useState(null);
  const [ouvert, toggle] = usePanneau();
  const societesDispo = useMemo(() => data?.societesDispo || [], [data]);
  const f = useFiltresFourn(societesDispo);

  useEffect(() => {
    let actif = true; setErreur(''); setOpen(null); setDrill({});
    api.analyse({ ...f.query, dimension }).then((r) => { if (actif) setData(r.data); }).catch((e) => { if (actif) setErreur(e.response?.data?.error || 'Erreur de chargement'); });
    return () => { actif = false; };
  }, [f.cle, dimension]); // eslint-disable-line react-hooks/exhaustive-deps

  const ouvrirDrill = async (nom) => {
    if (open === nom) { setOpen(null); return; }
    setOpen(nom);
    if (!drill[nom]) {
      setDrill((d) => ({ ...d, [nom]: 'loading' }));
      try { const r = await api.analyse({ ...f.query, dimension, drill: nom }); setDrill((d) => ({ ...d, [nom]: r.data.rows || [] })); }
      catch (e) { setDrill((d) => ({ ...d, [nom]: [] })); }
    }
  };
  const ouvrirEcritures = async (nom) => {
    setModale({ nom, lignes: null });
    try { const r = await api.detail({ ...f.query, nom }); setModale(r.data); }
    catch (e) { setModale({ nom, lignes: [], erreur: e.response?.data?.error || 'Erreur' }); }
  };

  const Panneau = <PanneauFourn ouvert={ouvert} onToggle={toggle} f={f} societesDispo={societesDispo} />;
  if (erreur) return <><main className="vt-page"><div className="bi-empty"><h2>Rapport indisponible</h2><p>{erreur}</p></div></main>{Panneau}</>;
  if (!data) return <><main className="vt-page"><div className="vt-skel" /></main>{Panneau}</>;

  const { mois, rows } = data;
  const st = statsDe(rows, mesure);
  const D = dimObj(dimension);
  const catFiltre = f.catsActives.length < CATS.length;
  const catLabel = catFiltre ? f.catsActives.map((c) => CATS.find((x) => x.code === c)?.nom).join(' + ') : null;
  const totMois = mois.map((_, i) => { let t = 0, t1 = 0; rows.forEach((r) => { t += r.m[i]; t1 += r.n1[i]; }); return mesure === 'montant' ? t : mesure === 'delta' ? (t1 ? t - t1 : null) : deltaPct(t, t1); });
  let T = 0, T1 = 0; rows.forEach((r) => { T += r.total; T1 += r.totalN1; });
  const totGen = mesure === 'montant' ? T : mesure === 'delta' ? (T1 ? T - T1 : null) : deltaPct(T, T1);
  const sousFourn = D.sous === 'fournisseur';

  const Cellule = ({ o, i }) => {
    const v = cellVal(o, i, mesure), c = couleur(v, mesure, st);
    return <td style={{ background: c.bg }} title={mesure !== 'montant' && o.n1[i] ? `${montant(o.m[i], unite)} vs ${montant(o.n1[i], unite)} en N-1` : undefined}><span className={`cell${c.mut ? ' mut' : ''}`} style={c.fg ? { color: c.fg } : undefined}>{fmt(v, mesure, unite)}</span></td>;
  };

  return (
    <>
      <main className="vt-page">
        <div className="vt-head">
          <div>
            <h1>Fournisseurs — Analyse croisée</h1>
          </div>
          <div className="vt-asof"><b>{data.periode}</b><br />vs mêmes mois N-1 · HT{catLabel ? <><br />{catLabel}</> : ''}</div>
        </div>
        <Avertissements data={data} />

        <div className="vt-ctl">
          <span><span className="lab">Mesure</span>
            <span className="vt-seg">{MESURES.map((m) => <button key={m.code} className={mesure === m.code ? 'on' : ''} onClick={() => setMesure(m.code)}>{m.label}</button>)}</span>
          </span>
          <span><span className="lab">Dimension</span>
            <span className="vt-seg">{DIMS.map((d) => <button key={d.code} className={dimension === d.code ? 'on' : ''} onClick={() => setDimension(d.code)}>{d.label}</button>)}</span>
          </span>
        </div>

        <div className="vt-card">
          <div className="vt-card-h"><h2>{MESURES.find((m) => m.code === mesure).label} — par {D.un} et par mois</h2>
            <div className="st-tools">{data.tronque && <span className="hint">{rows.length} premiers sur {data.tronque}</span>}
              {MONETAIRES.includes(mesure) && <SelecteurUnite unite={unite} setUnite={setUnite} />}
            </div>
          </div>
          <div className="vt-card-b vt-tbl-wrap">
            <table className="vt-mx tbl-dense">
              <thead><tr><th className="l">{D.label}</th>{mois.map((m) => <th key={m}>{moisCourt(m)}</th>)}<th className="tot">Période</th><th className="fr-mx-n1">N-1</th></tr></thead>
              <tbody>
                {rows.map((o) => {
                  const isOpen = open === o.nom;
                  const sub = drill[o.nom];
                  return (
                    <React.Fragment key={o.nom}>
                      <tr>
                        <td className={`l ${isOpen ? 'open' : ''}`} onClick={() => ouvrirDrill(o.nom)} title={o.nom}>
                          <span className="chev">▶</span>{o.nom}
                          {dimension === 'fournisseur' && <button className="fr-btn fr-btn-mx" title="Voir les écritures du grand livre" aria-label="Écritures" onClick={(e) => { e.stopPropagation(); ouvrirEcritures(o.nom); }}>≡</button>}
                        </td>
                        {mois.map((_, i) => <Cellule key={i} o={o} i={i} />)}
                        <td className="tot">{fmt(totVal(o, mesure), mesure, unite)}</td>
                        <td className="fr-mx-n1">{o.totalN1 ? montant(o.totalN1, unite) : '—'}</td>
                      </tr>
                      {isOpen && sub === 'loading' && <tr className="sub"><td className="l"><span className="chev" />Chargement…</td><td colSpan={mois.length + 2} /></tr>}
                      {isOpen && Array.isArray(sub) && sub.length === 0 && <tr className="sub"><td className="l"><span className="chev" />Aucun détail.</td><td colSpan={mois.length + 2} /></tr>}
                      {isOpen && Array.isArray(sub) && sub.map((mo) => (
                        <tr className="sub" key={mo.nom}>
                          <td className="l" title={mo.nom}><span className="chev" />{mo.nom}
                            {sousFourn && <button className="fr-btn fr-btn-mx" title="Voir les écritures du grand livre" aria-label="Écritures" onClick={() => ouvrirEcritures(mo.nom)}>≡</button>}
                          </td>
                          {mois.map((_, i) => <Cellule key={i} o={mo} i={i} />)}
                          <td className="tot">{fmt(totVal(mo, mesure), mesure, unite)}</td>
                          <td className="fr-mx-n1">{mo.totalN1 ? montant(mo.totalN1, unite) : '—'}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
                {rows.length === 0 && <tr><td className="l" colSpan={mois.length + 3} style={{ textAlign: 'center', color: 'var(--text-muted,#96a0b0)' }}>Aucune donnée.</td></tr>}
              </tbody>
              <tfoot><tr><td className="l">{data.tronque ? `Total des ${data.rows.length} lignes affichées` : 'Total'}</td>{totMois.map((v, i) => <td key={i}>{fmt(v, mesure, unite)}</td>)}<td className="tot">{fmt(totGen, mesure, unite)}</td><td className="fr-mx-n1">{montant(T1, unite)}</td></tr></tfoot>
            </table>
            <div className="vt-lgd">
              {mesure === 'montant' ? (
                <><span>Faible</span><Echelle /><span>Élevé</span></>
              ) : (
                <><span>Charge en baisse</span><Echelle divergent sens={-1} /><span>En hausse</span>&nbsp;·&nbsp;total période : <b>{montant(T, unite)}</b> vs <b>{montant(T1, unite)}</b> en N-1 ({signePct1(deltaPct(T, T1))})</>
              )}
            </div>
          </div>
        </div>
      </main>
      <ModaleEcritures drill={modale} onFermer={() => setModale(null)} />
      {Panneau}
    </>
  );
}
